/**
 * Lightweight i18n for the guest-facing invitation.
 *
 * Two layers:
 *  1. STRINGS — static UI strings (labels, buttons) keyed by id.
 *  2. localize() — reads couple-authored content from
 *     inviteConfig.translations[<lang>][<field>] with primary-language fallback.
 *
 * Couples write content in their primary language (English for now); optional
 * translations go under inviteConfig.translations.zh / .th / etc. Existing
 * fields stay non-breaking — translations is purely additive.
 */

export const LANGUAGES = [
  { code: 'en', name: 'English', native: 'English' },
  { code: 'zh', name: 'Chinese', native: '中文' },
  { code: 'th', name: 'Thai',    native: 'ไทย' },
];

export const DEFAULT_LANG = 'en';

const STRINGS = {
  en: {
    'rsvp.title': 'RSVP',
    'rsvp.subtitle': 'Please let us know if you can make it',
    'rsvp.update_title': 'Update RSVP',
    'rsvp.update_subtitle': 'Change your response below',
    'rsvp.your_name': 'Your name',
    'rsvp.name_placeholder': 'Enter your full name',
    'rsvp.attending_question': 'Will you be joining us?',
    'rsvp.yes': "Yes, I'll be there",
    'rsvp.no': "Sorry, I can't attend",
    'rsvp.accept_sub': "We'll celebrate together",
    'rsvp.decline_sub': 'Celebrating from afar',
    'rsvp.confirmed_eyebrow': 'Your response is confirmed',
    'rsvp.party_reserved': 'Reserved for {count} guests',
    'rsvp.companion_name_placeholder': 'Their full name',
    'rsvp.companion_name_hint': 'Please enter their actual name',
    'rsvp.dietary': 'Dietary preferences',
    'rsvp.optional': '(optional)',
    'rsvp.dietary_placeholder': 'e.g. Halal, Vegetarian (leave blank if none)',
    'rsvp.safety': 'Allergies or medical info',
    'rsvp.safety_placeholder': 'e.g. Nut allergy (EpiPen), Wheelchair, Diabetic',
    'rsvp.safety_question': 'Any allergies or medical needs we should know about?',
    // Shown before a guest answers anything: what is collected, who sees it,
    // how long it is kept. Guests are told, not asked to accept terms — the
    // Terms of Service are a contract with the couple, not with their guests.
    'rsvp.privacy_notice': 'Your answers go to the couple, and any dietary or allergy notes are shared with their caterer. They are deleted 30 days after the wedding.',
    'rsvp.privacy_link': 'Privacy Policy',
    'rsvp.safety_none': 'None',
    'rsvp.safety_yes': 'Yes',
    'rsvp.dietary_add': 'Add dietary preferences',
    'rsvp.dietary_other': 'Other (e.g. no mushrooms)',
    'rsvp.diet_halal': 'Halal',
    'rsvp.diet_vegetarian': 'Vegetarian',
    'rsvp.diet_vegan': 'Vegan',
    'rsvp.diet_no_beef': 'No beef',
    'rsvp.diet_no_pork': 'No pork/lard',
    'rsvp.diet_no_seafood': 'No seafood',
    'rsvp.plus_one_safety': 'Allergies or medical info (optional)',
    'rsvp.message_label': 'Message to the couple',
    'rsvp.message_placeholder_attending': 'Write a lovely note...',
    'rsvp.message_placeholder_declining': 'Send your well wishes...',
    'rsvp.add_plus_one': 'Add a plus-one',
    'rsvp.plus_one_details': 'Plus-one details',
    'rsvp.plus_one_name': 'Guest name',
    'rsvp.plus_one_dietary': 'Dietary preferences (optional)',
    'rsvp.confirm': 'Confirm Attendance',
    'rsvp.send_response': 'Send Response',
    'rsvp.update_response': 'Update Response',
    'rsvp.error_name': 'Please enter your name',
    'rsvp.error_response': 'Please select your response above',
    'rsvp.error_event_response': 'Please respond to each event above',
    'rsvp.multi_event_question': 'Which events will you attend?',
    'rsvp.event_yes': 'Yes, attending',
    'rsvp.event_no': "Can't make it",
    'rsvp.event_headcount': 'How many of your party?',
    'rsvp.deadline_prefix': 'Please respond by',
    'rsvp.already_attending_title': "You're confirmed!",
    'rsvp.already_declined_title': 'Thanks for responding',
    'rsvp.already_attending_body': "We can't wait to celebrate with you, {name}!",
    'rsvp.already_declined_body': "We'll miss you, {name}. Thank you for letting us know.",
    'rsvp.your_response': 'Your Response',
    'rsvp.attending_badge': 'Attending',
    'rsvp.not_attending_badge': 'Not Attending',
    'rsvp.dietary_label': 'Dietary:',
    'rsvp.parking_question': 'Will you need a parking coupon?',
    'rsvp.parking_cars': 'How many cars?',
    'rsvp.meal_label': 'Main course',
    'rsvp.parking_yes': 'Yes, I need parking',
    'rsvp.parking_no': 'No parking needed',
    'rsvp.kids_question': 'How many children?',
    'rsvp.highchairs_question': 'High chairs needed?',
    'rsvp.error_parking': 'Please indicate if you need parking',
    'rsvp.error_dietary': 'Please share your dietary preferences',
    'rsvp.error_submit': "We couldn't save your RSVP just now. Please try again in a moment.",
    'rsvp.error_submit_closed': 'This wedding is no longer accepting RSVPs. Please contact the couple directly.',
    'rsvp.error_network': "We couldn't reach the server. Please check your connection and try again.",
    'rsvp.deadline_passed': 'The RSVP deadline was {date}. You can still respond, and the couple will see your reply.',
    'rsvp.parking_label': 'Parking:',
    'rsvp.parking_yes_badge': 'Parking needed',
    'rsvp.parking_no_badge': 'No parking needed',
    'rsvp.plus_one_label': 'Plus one:',
    'rsvp.update_link': 'Update RSVP',
    'rsvp.come_back_day': 'Come back to this page on the wedding day to check in.',
    'rsvp.come_back_pre': 'Come back on the wedding day for the check-in link!',
    'rsvp.event_details': 'Event Details',
    'rsvp.whats_next': "What's next",
    'rsvp.add_to_calendar': 'Add to Calendar',
    'rsvp.see_you': 'See You There!',
    'rsvp.miss_you': "We'll Miss You!",
    'rsvp.thanks_attending': "Thank you, {name}. We can't wait to celebrate with you!",
    'rsvp.thanks_declining': 'Thank you for letting us know, {name}. We wish you all the best.',
    'invite.bar_rsvp': 'RSVP',
    'invite.bar_rsvp_by': 'by {date}',
    'invite.bar_going': "You're going",
    'invite.bar_received': 'Response received',
    'invite.bar_update': 'Update',
    'invite.bar_find': 'Enter invitation code',
    'invite.days_to_go': '{n} days to go',
    'invite.day_to_go': '1 day to go',
    'invite.countdown_days': 'Days',
    'invite.countdown_hours': 'Hours',
    'invite.countdown_minutes': 'Minutes',
    'invite.countdown_seconds': 'Seconds',
    'invite.whatsapp_help': 'Questions? WhatsApp us',
    'invite.see_you_there': 'See you there',
    'invite.directions': 'Directions',
    'invite.search_title': 'Find Your Invitation',
    'invite.search_subtitle': 'Enter your full name as it appears on the guest list',
    'invite.search_button': 'Find My Invitation',
    'invite.search_placeholder': 'Your full name...',
    'invite.search_is_this_you': 'Is this you?',
    'invite.search_none': 'None of these are me',
    'invite.search_not_found_title': "We couldn't find your name",
    'invite.search_not_found_body': 'Please check the spelling of your name, or contact {couple} directly. On the wedding day, head to the reception desk for assistance.',
    'invite.search_try_again': 'Try Again',
    'invite.loading': 'Loading invitation...',
    'invite.invalid_link': 'This RSVP link is invalid or has expired.',
    'invite.contact_couple': 'Please contact the couple for a new invitation link.',
    'invite.error_not_found': "We couldn't find this invitation.",
    'invite.error_not_found_detail': 'The link may contain a typo, or this wedding may no longer be active.',
    'invite.error_purged': 'This wedding’s invitation has been removed.',
    'invite.error_purged_detail': 'The couple has removed their wedding data.',
    'invite.error_server': 'Something went wrong on our end.',
    'invite.error_server_detail': 'Please try again in a moment.',
    'invite.error_network': 'We couldn’t reach the server.',
    'invite.error_network_detail': 'Please check your internet connection and try again.',
    'invite.retry': 'Try Again',
    'invite.opening': 'Opening your invitation...',
    'invite.tap_to_open': 'Tap to open',
    'invite.code_title': 'Your invitation',
    'invite.code_subtitle': 'Enter your invitation code',
    'invite.code_button': 'Open my invitation',
    'invite.code_error': "That code doesn't look right. Please check it and try again.",
    'invite.code_throttled': 'Too many attempts. Please wait a minute and try again.',
    'invite.code_help': 'Your code is in the message from {couple}. Anyone in your group can use it to reply for everyone.',
    'rsvp.extra_pending': 'Your request for additional guests is with the couple.',
    'rsvp.request_extra': 'Anyone else accompanying you',
    'rsvp.request_extra_name': 'Their name (optional)',
    'rsvp.request_extra_add': 'Add another',
    'rsvp.request_extra_note': 'A note for the couple (optional)',
    'rsvp.request_extra_hint': "The couple will confirm with you after checking available seats.",
    'invite.music_play': 'Play music',
    'invite.music_pause': 'Pause music',
    'invite.coming_soon_title': 'Invitation Coming Soon',
    'invite.coming_soon_body': 'Check back later for the wedding details.',
    'invite.adults_only': 'This is an adults-only celebration',
    'invite.children_welcome': 'Children are welcome!',
    'invite.today_eyebrow': 'Today is the day',
    'invite.checkin_intro': "We're so glad you're here! Tap below to check in.",
    'invite.checkin_cta': 'Go to Check-in',
    'invite.checkin_outro': 'Search your name, take a selfie, and find your table!',
    'invite.dear': 'Dear {name},',
  },
  zh: {
    'rsvp.title': '出席回复',
    'rsvp.subtitle': '请告知您能否出席',
    'rsvp.update_title': '更新回复',
    'rsvp.update_subtitle': '在下方更改您的回复',
    'rsvp.your_name': '您的姓名',
    'rsvp.name_placeholder': '请输入您的全名',
    'rsvp.attending_question': '您能出席婚礼吗？',
    'rsvp.yes': '是的，我会出席',
    'rsvp.no': '抱歉，无法出席',
    'rsvp.accept_sub': '我们一同庆祝',
    'rsvp.decline_sub': '遥祝新婚快乐',
    'rsvp.confirmed_eyebrow': '您的回复已确认',
    'rsvp.party_reserved': '已为 {count} 位宾客预留',
    'rsvp.companion_name_placeholder': '宾客全名',
    'rsvp.companion_name_hint': '请输入真实姓名',
    'rsvp.dietary': '饮食偏好',
    'rsvp.optional': '（选填）',
    'rsvp.dietary_placeholder': '例如：清真、素食、不吃海鲜（没有可留空）',
    'rsvp.safety': '过敏或医疗信息',
    'rsvp.safety_placeholder': '例如：坚果过敏（携带肾上腺素笔）、使用轮椅、糖尿病',
    'rsvp.safety_question': '有需要我们注意的过敏或医疗情况吗？',
    'rsvp.privacy_notice': '您的回覆将提供给新人，饮食或过敏信息会转告他们的餐饮供应商。这些资料将在婚礼后 30 天删除。',
    'rsvp.privacy_link': '隐私政策',
    'rsvp.safety_none': '没有',
    'rsvp.safety_yes': '有',
    'rsvp.dietary_add': '添加饮食偏好',
    'rsvp.dietary_other': '其他：例如不吃蘑菇',
    'rsvp.diet_halal': '清真',
    'rsvp.diet_vegetarian': '素食',
    'rsvp.diet_vegan': '纯素',
    'rsvp.diet_no_beef': '不吃牛肉',
    'rsvp.diet_no_pork': '不吃猪肉/猪油',
    'rsvp.diet_no_seafood': '不吃海鲜',
    'rsvp.plus_one_safety': '过敏或医疗信息（选填）',
    'rsvp.message_label': '给新人的留言',
    'rsvp.message_placeholder_attending': '写下您的祝福……',
    'rsvp.message_placeholder_declining': '送上您的祝福……',
    'rsvp.add_plus_one': '添加同行宾客',
    'rsvp.plus_one_details': '同行宾客信息',
    'rsvp.plus_one_name': '同行宾客姓名',
    'rsvp.plus_one_dietary': '饮食偏好（选填）',
    'rsvp.confirm': '确认出席',
    'rsvp.send_response': '发送回复',
    'rsvp.update_response': '更新回复',
    'rsvp.error_name': '请输入您的姓名',
    'rsvp.error_response': '请选择您的回复',
    'rsvp.error_event_response': '请回复以上每个环节',
    'rsvp.multi_event_question': '您将出席哪些环节？',
    'rsvp.event_yes': '出席',
    'rsvp.event_no': '无法出席',
    'rsvp.event_headcount': '您一行几位出席？',
    'rsvp.deadline_prefix': '请于此日期前回复：',
    'rsvp.already_attending_title': '您已确认出席！',
    'rsvp.already_declined_title': '感谢您的回复',
    'rsvp.already_attending_body': '{name}，我们期待与您共同庆祝！',
    'rsvp.already_declined_body': '{name}，我们会想念您。感谢您的告知。',
    'rsvp.your_response': '您的回复',
    'rsvp.attending_badge': '出席',
    'rsvp.not_attending_badge': '不出席',
    'rsvp.dietary_label': '饮食：',
    'rsvp.parking_question': '您需要停车券吗？',
    'rsvp.parking_cars': '几辆车？',
    'rsvp.meal_label': '主菜',
    'rsvp.parking_yes': '是的，我需要停车',
    'rsvp.parking_no': '不需要停车',
    'rsvp.kids_question': '有多少位小孩？',
    'rsvp.highchairs_question': '需要几张儿童高脚椅？',
    'rsvp.error_parking': '请告知您是否需要停车',
    'rsvp.error_dietary': '请填写您的饮食偏好',
    'rsvp.error_submit': '暂时无法保存您的回复，请稍后再试。',
    'rsvp.error_submit_closed': '此婚礼已不再接受回复，请直接联系新人。',
    'rsvp.error_network': '无法连接服务器，请检查网络后重试。',
    'rsvp.deadline_passed': '回复截止日期为 {date}，您仍可回复，新人会看到您的答复。',
    'rsvp.parking_label': '停车：',
    'rsvp.parking_yes_badge': '需要停车',
    'rsvp.parking_no_badge': '不需要停车',
    'rsvp.plus_one_label': '同行宾客：',
    'rsvp.update_link': '更新回复',
    'rsvp.come_back_day': '请在婚礼当天回到此页面办理签到。',
    'rsvp.come_back_pre': '请在婚礼当天回到此页面获取签到链接！',
    'rsvp.event_details': '婚礼详情',
    'rsvp.whats_next': '接下来',
    'rsvp.add_to_calendar': '添加到日历',
    'rsvp.see_you': '婚礼见！',
    'rsvp.miss_you': '我们会想念您！',
    'rsvp.thanks_attending': '感谢{name}，我们期待与您共同庆祝！',
    'rsvp.thanks_declining': '{name}，感谢您的告知，祝您一切顺利。',
    'invite.bar_rsvp': '回复邀请',
    'invite.bar_rsvp_by': '截止 {date}',
    'invite.bar_going': '您将出席',
    'invite.bar_received': '已收到回复',
    'invite.bar_update': '修改',
    'invite.bar_find': '输入邀请码',
    'invite.days_to_go': '还有 {n} 天',
    'invite.day_to_go': '还有 1 天',
    'invite.countdown_days': '天',
    'invite.countdown_hours': '小时',
    'invite.countdown_minutes': '分钟',
    'invite.countdown_seconds': '秒',
    'invite.whatsapp_help': '有疑问？通过 WhatsApp 联系我们',
    'invite.see_you_there': '到时见',
    'invite.directions': '导航',
    'invite.search_title': '查找您的邀请',
    'invite.search_subtitle': '请输入与宾客名单上一致的全名',
    'invite.search_button': '查找我的邀请',
    'invite.search_placeholder': '您的全名……',
    'invite.search_is_this_you': '这是您吗？',
    'invite.search_none': '这些都不是我',
    'invite.search_not_found_title': '未能找到您的姓名',
    'invite.search_not_found_body': '请检查姓名拼写，或直接联系{couple}。婚礼当天可前往签到台寻求协助。',
    'invite.search_try_again': '重试',
    'invite.loading': '正在加载邀请……',
    'invite.invalid_link': '此回复链接无效或已过期。',
    'invite.contact_couple': '请联系新人获取新的邀请链接。',
    'invite.error_not_found': '未能找到此邀请。',
    'invite.error_not_found_detail': '链接可能包含错别字，或此婚礼已不再有效。',
    'invite.error_purged': '此婚礼的邀请已被移除。',
    'invite.error_purged_detail': '新人已删除其婚礼数据。',
    'invite.error_server': '我们这边出现了问题。',
    'invite.error_server_detail': '请稍后再试。',
    'invite.error_network': '无法连接到服务器。',
    'invite.error_network_detail': '请检查您的网络连接并重试。',
    'invite.retry': '重试',
    'invite.opening': '正在打开您的邀请……',
    'invite.tap_to_open': '轻触开启',
    'invite.code_title': '您的邀请函',
    'invite.code_subtitle': '请输入您的邀请码',
    'invite.code_button': '打开我的邀请函',
    'invite.code_error': '邀请码似乎不正确，请检查后重试。',
    'invite.code_throttled': '尝试次数过多，请稍等一分钟后再试。',
    'invite.code_help': '邀请码在 {couple} 发给您的信息中。同组任何人都可以用它为全组回复。',
    'rsvp.extra_pending': '您新增宾客的请求已发送给新人。',
    'rsvp.request_extra': '还有其他人与您同行吗',
    'rsvp.request_extra_name': '宾客姓名（选填）',
    'rsvp.request_extra_add': '再添加一位',
    'rsvp.request_extra_note': '给新人的备注（选填）',
    'rsvp.request_extra_hint': '新人会与您确认——他们需要先确认座位安排。',
    'invite.music_play': '播放音乐',
    'invite.music_pause': '暂停音乐',
    'invite.coming_soon_title': '邀请即将推出',
    'invite.coming_soon_body': '请稍后查看婚礼详情。',
    'invite.adults_only': '本次婚礼仅限成人参加',
    'invite.children_welcome': '欢迎携带孩童出席！',
    'invite.today_eyebrow': '今日大喜',
    'invite.checkin_intro': '欢迎光临！请点击下方按钮签到。',
    'invite.checkin_cta': '前往签到',
    'invite.checkin_outro': '搜索您的姓名、拍张自拍、找到您的座位！',
    'invite.dear': '亲爱的{name}：',
  },
  th: {
    'rsvp.title': 'ตอบรับคำเชิญ',
    'rsvp.subtitle': 'โปรดแจ้งให้เราทราบว่าท่านสามารถมาร่วมงานได้หรือไม่',
    'rsvp.update_title': 'อัปเดตการตอบรับ',
    'rsvp.update_subtitle': 'เปลี่ยนคำตอบของท่านด้านล่าง',
    'rsvp.your_name': 'ชื่อของท่าน',
    'rsvp.name_placeholder': 'กรอกชื่อ-นามสกุลเต็ม',
    'rsvp.attending_question': 'ท่านจะมาร่วมงานหรือไม่?',
    'rsvp.yes': 'ใช่ ฉันจะไปร่วมงาน',
    'rsvp.no': 'ขออภัย ไม่สามารถมาได้',
    'rsvp.accept_sub': 'ร่วมฉลองไปด้วยกัน',
    'rsvp.decline_sub': 'ร่วมยินดีจากแดนไกล',
    'rsvp.confirmed_eyebrow': 'บันทึกคำตอบของท่านแล้ว',
    'rsvp.party_reserved': 'สำรองที่นั่งสำหรับ {count} ท่าน',
    'rsvp.companion_name_placeholder': 'ชื่อ-นามสกุล',
    'rsvp.companion_name_hint': 'กรุณากรอกชื่อจริง',
    'rsvp.dietary': 'ข้อจำกัดทางอาหาร',
    'rsvp.optional': '(ไม่บังคับ)',
    'rsvp.dietary_placeholder': 'เช่น ฮาลาล มังสวิรัติ (เว้นว่างได้หากไม่มี)',
    'rsvp.safety': 'ข้อมูลการแพ้หรือการแพทย์',
    'rsvp.safety_placeholder': 'เช่น แพ้ถั่ว (มี EpiPen) ใช้รถเข็น เป็นเบาหวาน',
    'rsvp.safety_question': 'มีอาการแพ้หรือความต้องการทางการแพทย์ที่ควรแจ้งให้ทราบหรือไม่?',
    'rsvp.privacy_notice': 'คำตอบของคุณจะส่งถึงคู่บ่าวสาว และข้อมูลด้านอาหารหรือการแพ้จะแจ้งต่อผู้จัดเลี้ยง ข้อมูลจะถูกลบ 30 วันหลังงานแต่งงาน',
    'rsvp.privacy_link': 'นโยบายความเป็นส่วนตัว',
    'rsvp.safety_none': 'ไม่มี',
    'rsvp.safety_yes': 'มี',
    'rsvp.dietary_add': 'เพิ่มข้อจำกัดทางอาหาร',
    'rsvp.dietary_other': 'อื่น ๆ เช่น ไม่ทานเห็ด',
    'rsvp.diet_halal': 'ฮาลาล',
    'rsvp.diet_vegetarian': 'มังสวิรัติ',
    'rsvp.diet_vegan': 'วีแกน',
    'rsvp.diet_no_beef': 'ไม่ทานเนื้อวัว',
    'rsvp.diet_no_pork': 'ไม่ทานหมู/น้ำมันหมู',
    'rsvp.diet_no_seafood': 'ไม่ทานอาหารทะเล',
    'rsvp.plus_one_safety': 'ข้อมูลการแพ้หรือการแพทย์ (ไม่บังคับ)',
    'rsvp.message_label': 'ข้อความถึงคู่บ่าวสาว',
    'rsvp.message_placeholder_attending': 'เขียนข้อความแสดงความยินดี...',
    'rsvp.message_placeholder_declining': 'ส่งคำอวยพร...',
    'rsvp.add_plus_one': 'เพิ่มผู้ติดตาม',
    'rsvp.plus_one_details': 'ข้อมูลผู้ติดตาม',
    'rsvp.plus_one_name': 'ชื่อผู้ติดตาม',
    'rsvp.plus_one_dietary': 'ข้อจำกัดทางอาหาร (ไม่บังคับ)',
    'rsvp.confirm': 'ยืนยันการเข้าร่วม',
    'rsvp.send_response': 'ส่งคำตอบ',
    'rsvp.update_response': 'อัปเดตคำตอบ',
    'rsvp.error_name': 'โปรดกรอกชื่อของท่าน',
    'rsvp.error_response': 'โปรดเลือกคำตอบด้านบน',
    'rsvp.error_event_response': 'โปรดตอบทุกกิจกรรมด้านบน',
    'rsvp.multi_event_question': 'ท่านจะเข้าร่วมกิจกรรมใดบ้าง?',
    'rsvp.event_yes': 'เข้าร่วม',
    'rsvp.event_no': 'ไม่สามารถมาได้',
    'rsvp.event_headcount': 'มากันกี่ท่าน?',
    'rsvp.deadline_prefix': 'โปรดตอบกลับภายใน',
    'rsvp.already_attending_title': 'ยืนยันการเข้าร่วมแล้ว!',
    'rsvp.already_declined_title': 'ขอบคุณสำหรับคำตอบ',
    'rsvp.already_attending_body': 'เรารอที่จะฉลองร่วมกับคุณ {name}!',
    'rsvp.already_declined_body': 'เราจะคิดถึงคุณ {name} ขอบคุณที่แจ้งให้ทราบ',
    'rsvp.your_response': 'คำตอบของท่าน',
    'rsvp.attending_badge': 'เข้าร่วม',
    'rsvp.not_attending_badge': 'ไม่เข้าร่วม',
    'rsvp.dietary_label': 'อาหาร:',
    'rsvp.parking_question': 'ท่านต้องการคูปองจอดรถหรือไม่?',
    'rsvp.parking_cars': 'กี่คัน?',
    'rsvp.meal_label': 'จานหลัก',
    'rsvp.parking_yes': 'ใช่ ต้องการที่จอดรถ',
    'rsvp.parking_no': 'ไม่ต้องการที่จอดรถ',
    'rsvp.kids_question': 'มีเด็กกี่คน?',
    'rsvp.highchairs_question': 'ต้องการเก้าอี้เด็กกี่ตัว?',
    'rsvp.error_parking': 'โปรดระบุว่าท่านต้องการที่จอดรถหรือไม่',
    'rsvp.error_dietary': 'โปรดระบุข้อจำกัดทางอาหาร',
    'rsvp.error_submit': 'ไม่สามารถบันทึกการตอบรับได้ในขณะนี้ โปรดลองอีกครั้ง',
    'rsvp.error_submit_closed': 'งานแต่งงานนี้ไม่รับการตอบรับแล้ว โปรดติดต่อคู่บ่าวสาวโดยตรง',
    'rsvp.error_network': 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้ โปรดตรวจสอบการเชื่อมต่อแล้วลองอีกครั้ง',
    'rsvp.deadline_passed': 'กำหนดตอบรับคือ {date} ท่านยังคงตอบรับได้ และคู่บ่าวสาวจะเห็นคำตอบของท่าน',
    'rsvp.parking_label': 'ที่จอดรถ:',
    'rsvp.parking_yes_badge': 'ต้องการที่จอดรถ',
    'rsvp.parking_no_badge': 'ไม่ต้องการที่จอดรถ',
    'rsvp.plus_one_label': 'ผู้ติดตาม:',
    'rsvp.update_link': 'อัปเดตการตอบรับ',
    'rsvp.come_back_day': 'กลับมาที่หน้านี้ในวันงานเพื่อเช็คอิน',
    'rsvp.come_back_pre': 'กลับมาที่หน้านี้ในวันงานเพื่อเช็คอิน!',
    'rsvp.event_details': 'รายละเอียดงาน',
    'rsvp.whats_next': 'ขั้นตอนต่อไป',
    'rsvp.add_to_calendar': 'เพิ่มในปฏิทิน',
    'rsvp.see_you': 'พบกันในงาน!',
    'rsvp.miss_you': 'เราจะคิดถึงคุณ!',
    'rsvp.thanks_attending': 'ขอบคุณ {name} เรารอที่จะฉลองร่วมกัน!',
    'rsvp.thanks_declining': 'ขอบคุณที่แจ้งให้ทราบ {name} ขอให้ทุกอย่างราบรื่น',
    'invite.bar_rsvp': 'ตอบรับคำเชิญ',
    'invite.bar_rsvp_by': 'ภายใน {date}',
    'invite.bar_going': 'ท่านจะมาร่วมงาน',
    'invite.bar_received': 'ได้รับคำตอบแล้ว',
    'invite.bar_update': 'แก้ไข',
    'invite.bar_find': 'กรอกรหัสคำเชิญ',
    'invite.days_to_go': 'อีก {n} วัน',
    'invite.day_to_go': 'อีก 1 วัน',
    'invite.countdown_days': 'วัน',
    'invite.countdown_hours': 'ชั่วโมง',
    'invite.countdown_minutes': 'นาที',
    'invite.countdown_seconds': 'วินาที',
    'invite.whatsapp_help': 'มีคำถาม? ติดต่อเราทาง WhatsApp',
    'invite.see_you_there': 'แล้วพบกัน',
    'invite.directions': 'นำทาง',
    'invite.search_title': 'ค้นหาคำเชิญของท่าน',
    'invite.search_subtitle': 'กรอกชื่อ-นามสกุลตามที่ปรากฏในรายชื่อแขก',
    'invite.search_button': 'ค้นหาคำเชิญของฉัน',
    'invite.search_placeholder': 'ชื่อ-นามสกุลของท่าน...',
    'invite.search_is_this_you': 'ใช่ท่านหรือไม่?',
    'invite.search_none': 'ไม่ใช่ฉันสักคน',
    'invite.search_not_found_title': 'ไม่พบชื่อของท่าน',
    'invite.search_not_found_body': 'โปรดตรวจสอบการสะกดชื่อ หรือติดต่อ {couple} โดยตรง ในวันงานสามารถมาที่จุดต้อนรับเพื่อขอความช่วยเหลือ',
    'invite.search_try_again': 'ลองอีกครั้ง',
    'invite.loading': 'กำลังโหลดคำเชิญ...',
    'invite.invalid_link': 'ลิงก์ตอบรับนี้ไม่ถูกต้องหรือหมดอายุแล้ว',
    'invite.contact_couple': 'โปรดติดต่อคู่บ่าวสาวเพื่อขอลิงก์ใหม่',
    'invite.error_not_found': 'ไม่พบคำเชิญนี้',
    'invite.error_not_found_detail': 'ลิงก์อาจมีการพิมพ์ผิด หรืองานแต่งงานนี้อาจไม่มีอยู่แล้ว',
    'invite.error_purged': 'คำเชิญสำหรับงานแต่งงานนี้ถูกลบออกแล้ว',
    'invite.error_purged_detail': 'คู่บ่าวสาวได้ลบข้อมูลงานแต่งงานของตนออกแล้ว',
    'invite.error_server': 'เกิดข้อผิดพลาดทางเซิร์ฟเวอร์',
    'invite.error_server_detail': 'โปรดลองอีกครั้งในอีกสักครู่',
    'invite.error_network': 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้',
    'invite.error_network_detail': 'โปรดตรวจสอบการเชื่อมต่ออินเทอร์เน็ตของท่านแล้วลองอีกครั้ง',
    'invite.retry': 'ลองอีกครั้ง',
    'invite.opening': 'กำลังเปิดคำเชิญของท่าน...',
    'invite.tap_to_open': 'แตะเพื่อเปิด',
    'invite.code_title': 'คำเชิญของท่าน',
    'invite.code_subtitle': 'กรอกรหัสคำเชิญของท่าน',
    'invite.code_button': 'เปิดคำเชิญของฉัน',
    'invite.code_error': 'รหัสไม่ถูกต้อง กรุณาตรวจสอบและลองอีกครั้ง',
    'invite.code_throttled': 'พยายามหลายครั้งเกินไป กรุณารอสักครู่แล้วลองใหม่',
    'invite.code_help': 'รหัสอยู่ในข้อความจาก {couple} สมาชิกในกลุ่มคนใดก็ได้สามารถใช้ตอบแทนทั้งกลุ่ม',
    'rsvp.extra_pending': 'คำขอเพิ่มแขกของท่านถูกส่งถึงคู่บ่าวสาวแล้ว',
    'rsvp.request_extra': 'มีใครมาร่วมงานกับท่านอีกไหม',
    'rsvp.request_extra_name': 'ชื่อของแขก (ไม่บังคับ)',
    'rsvp.request_extra_add': 'เพิ่มอีกหนึ่งท่าน',
    'rsvp.request_extra_note': 'ข้อความถึงคู่บ่าวสาว (ไม่บังคับ)',
    'rsvp.request_extra_hint': 'คู่บ่าวสาวจะยืนยันกับท่านอีกครั้ง เนื่องจากต้องตรวจสอบที่นั่งก่อน',
    'invite.music_play': 'เล่นเพลง',
    'invite.music_pause': 'หยุดเพลง',
    'invite.coming_soon_title': 'คำเชิญจะเปิดเร็วๆ นี้',
    'invite.coming_soon_body': 'กลับมาดูรายละเอียดงานภายหลัง',
    'invite.adults_only': 'งานนี้สำหรับผู้ใหญ่เท่านั้น',
    'invite.children_welcome': 'ยินดีต้อนรับเด็กๆ!',
    'invite.today_eyebrow': 'วันนี้คือวันสำคัญ',
    'invite.checkin_intro': 'ดีใจที่ท่านมาร่วมงาน! แตะด้านล่างเพื่อเช็คอิน',
    'invite.checkin_cta': 'ไปที่จุดเช็คอิน',
    'invite.checkin_outro': 'ค้นหาชื่อ ถ่ายเซลฟี่ และหาโต๊ะของท่าน!',
    'invite.dear': 'เรียน {name}',
  },
};

/**
 * Look up a static UI string. Falls back to English then to the key itself.
 * Supports {placeholder} interpolation.
 */
export function t(key, lang = DEFAULT_LANG, vars) {
  const raw = STRINGS[lang]?.[key] || STRINGS[DEFAULT_LANG]?.[key] || key;
  if (!vars) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, k) => (vars[k] ?? `{${k}}`));
}

/**
 * Read a couple-authored field with translation fallback.
 * inviteConfig.translations[lang][field] → inviteConfig[field] → ''
 */
export function localize(inviteConfig, field, lang) {
  if (!inviteConfig) return '';
  const primary = inviteConfig[field] ?? '';
  if (!lang || lang === DEFAULT_LANG) return primary;
  return inviteConfig.translations?.[lang]?.[field] || primary;
}

/**
 * Produce a localized view of inviteConfig for the chosen language.
 * Flat string fields in translations[lang] override their primaries when
 * non-empty. Events are merged per-id (translations[lang].events[id]).
 * Falls through to the primary inviteConfig when no translations exist.
 */
export function mergeTranslations(inviteConfig, lang) {
  if (!inviteConfig || !lang || lang === DEFAULT_LANG) return inviteConfig;
  const tr = inviteConfig.translations?.[lang];
  if (!tr) return inviteConfig;
  const flat = {};
  for (const [k, v] of Object.entries(tr)) {
    if (k === 'events') continue;
    if (typeof v === 'string') {
      if (v.trim()) flat[k] = v;
    } else if (v != null) {
      flat[k] = v;
    }
  }
  let events = inviteConfig.events;
  if (Array.isArray(events) && tr.events) {
    events = events.map(e => {
      const evtTr = tr.events[e.id];
      if (!evtTr) return e;
      const merged = { ...e };
      for (const [k, v] of Object.entries(evtTr)) {
        if (typeof v === 'string' && v.trim()) merged[k] = v;
      }
      return merged;
    });
  }
  return { ...inviteConfig, ...flat, events };
}

const STORAGE_KEY = 'gw-invite-lang';

export function detectLanguage() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && LANGUAGES.some(l => l.code === saved)) return saved;
  } catch { /* storage unavailable */ }
  if (typeof navigator !== 'undefined') {
    const browserLang = (navigator.language || '').toLowerCase();
    if (browserLang.startsWith('zh')) return 'zh';
    if (browserLang.startsWith('th')) return 'th';
  }
  return DEFAULT_LANG;
}

export function persistLanguage(lang) {
  try { localStorage.setItem(STORAGE_KEY, lang); } catch { /* ignore */ }
}
